import CardTitleWithIcon from "@/components/company/card-title-with-icon";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { useTranslation } from "@/hooks/useTranslation";
import {
  buildWorkforceTimeline,
  hasHeadcountHistoryData,
  sumRecentDepartures,
  type WorkforceMonth,
} from "@/lib/company-events";
import { computeYoy, formatDateShortMonth } from "@/lib/format-amount";
import { cn } from "@/lib/utils";
import { CompanyDTO } from "@shaarp/common";
import { TrendingUpIcon } from "lucide-react";
import { useMemo } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from "recharts";

/** Departures are summed over the last year, as the turnover is read. */
const DEPARTURES_WINDOW_MONTHS = 12;

const HEADCOUNT_COLOR = "hsl(217, 91%, 60%)";
const DEPARTURES_COLOR = "hsl(0, 72%, 63%)";

type CardCompanyHeadcountHistoryProps = {
  readonly company: CompanyDTO;
};

type StatProps = {
  readonly label: string;
  readonly value: string;
  readonly className?: string;
};

function Stat({ label, value, className }: StatProps) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={cn("text-base font-semibold", className)}>{value}</span>
    </div>
  );
}

/** "2024-03" as the month it names, in the user's language. */
function formatMonth(month: string, language: string): string {
  return formatDateShortMonth(`${month}-01`, language);
}

type TimelineProps = {
  readonly timeline: WorkforceMonth[];
};

/** The current headcount, its change since the first month, and the year's departures. */
function HeadcountStats({
  timeline,
  departuresHistory,
}: TimelineProps & {
  readonly departuresHistory: CompanyDTO["departuresHistory"];
}) {
  const { t, currentLanguage } = useTranslation();
  const first = timeline.find((month) => month.headcount != null);
  const last = [...timeline].reverse().find((month) => month.headcount != null);
  const change = computeYoy(last?.headcount, first?.headcount);
  const departures = sumRecentDepartures(
    departuresHistory,
    timeline[timeline.length - 1]?.month ?? null,
    DEPARTURES_WINDOW_MONTHS,
  );

  return (
    <div className="grid grid-cols-3 gap-3">
      {last?.headcount != null && (
        <Stat
          label={t("company.insights.headcountHistory.current")}
          value={last.headcount.toLocaleString(currentLanguage)}
        />
      )}
      {change != null && first && (
        <Stat
          label={t("company.insights.headcountHistory.change", {
            since: formatMonth(first.month, currentLanguage),
          })}
          value={new Intl.NumberFormat(currentLanguage, {
            style: "percent",
            maximumFractionDigits: 0,
            signDisplay: "exceptZero",
          }).format(change / 100)}
          className={cn(
            change > 0 && "text-green-600",
            change < 0 && "text-red-600",
          )}
        />
      )}
      {departures != null && (
        <Stat
          label={t("company.insights.headcountHistory.recentDepartures")}
          value={departures.toLocaleString(currentLanguage)}
        />
      )}
    </div>
  );
}

/** The headcount as a line, the departures as bars on their own axis. */
function WorkforceChart({ timeline }: TimelineProps) {
  const { t, currentLanguage } = useTranslation();
  const hasDepartures = timeline.some((month) => month.departures != null);
  const chartConfig: ChartConfig = {
    headcount: {
      label: t("company.insights.headcountHistory.headcount"),
      color: HEADCOUNT_COLOR,
    },
    departures: {
      label: t("company.insights.headcountHistory.departures"),
      color: DEPARTURES_COLOR,
    },
  };
  const monthLabel = (month: string): string =>
    formatMonth(month, currentLanguage);

  return (
    <ChartContainer config={chartConfig} className="h-[220px] w-full">
      <ComposedChart
        data={timeline}
        margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
      >
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="month"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={24}
          tickFormatter={monthLabel}
        />
        <YAxis
          yAxisId="headcount"
          tickLine={false}
          axisLine={false}
          width={40}
          domain={["auto", "auto"]}
        />
        {hasDepartures && (
          <YAxis
            yAxisId="departures"
            orientation="right"
            tickLine={false}
            axisLine={false}
            width={28}
            allowDecimals={false}
          />
        )}
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelFormatter={(_, payload) =>
                monthLabel(String(payload?.[0]?.payload?.month ?? ""))
              }
            />
          }
        />
        {hasDepartures && (
          <Bar
            yAxisId="departures"
            dataKey="departures"
            fill="var(--color-departures)"
            radius={[3, 3, 0, 0]}
            maxBarSize={14}
          />
        )}
        <Line
          yAxisId="headcount"
          dataKey="headcount"
          type="monotone"
          stroke="var(--color-headcount)"
          strokeWidth={2}
          dot={false}
          connectNulls
        />
      </ComposedChart>
    </ChartContainer>
  );
}

/**
 * The headcount LinkedIn profiles gave month by month, with the employees who
 * left each month, as Coresignal counts them.
 */
export default function CardCompanyHeadcountHistory({
  company,
}: CardCompanyHeadcountHistoryProps) {
  const { t } = useTranslation();
  const timeline = useMemo(() => buildWorkforceTimeline(company), [company]);

  if (!hasHeadcountHistoryData(company)) return null;

  return (
    <Card className="flex flex-col min-w-0 overflow-hidden">
      <CardHeader>
        <CardTitleWithIcon
          icon={TrendingUpIcon}
          title={t("company.insights.headcountHistory.title")}
        />
        <p className="text-xs text-muted-foreground pl-[52px] -mt-1">
          {t("company.insights.headcountHistory.subtitle")}
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <HeadcountStats
          timeline={timeline}
          departuresHistory={company.departuresHistory}
        />
        <WorkforceChart timeline={timeline} />
      </CardContent>
    </Card>
  );
}
